import { Suspense, lazy } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/home.css";

const Hero3D = lazy(() => import("../components/Hero3D"));

const outcomes = [
  { n: "970", u: "K", label: "SEC entities indexed" },
  { n: "15", u: "M+", label: "Artifacts structured" },
  { n: "0", u: "", label: "Inferred fields" },
  { n: "5", u: "", label: "Products shipping" },
];

const fronts = [
  {
    index: "01",
    name: "StockLens",
    cat: "Financial intelligence",
    status: "Live",
    statusClass: "is-live",
    who: "The intelligence hedge funds pay fortunes for, made accessible and traceable to the filing.",
    metric: "970K",
    metricLabel: "SEC entities",
    href: "https://www.stocklenshq.com",
  },
  {
    index: "02",
    name: "MHX",
    cat: "Agentic search",
    status: "In research",
    statusClass: "is-research",
    who: "Answers built for everyone, cited and backed by evidence, never gatekept.",
    metric: "Cited",
    metricLabel: "Every answer",
  },
  {
    index: "03",
    name: "NetworkX",
    cat: "Trusted networks",
    status: "In development",
    statusClass: "is-building",
    who: "Opportunity that travels person to person through real, trusted connections.",
    metric: "P2P",
    metricLabel: "Real trust",
    href: "https://networkxjobs.com/",
  },
  {
    index: "04",
    name: "Bill-y",
    cat: "FinOps for AI",
    status: "In development",
    statusClass: "is-building",
    who: "Cost clarity so smaller teams are not flying blind on what AI really costs.",
    metric: "Spend",
    metricLabel: "Made visible",
    to: "/bill-y",
  },
  {
    index: "05",
    name: "iLab",
    cat: "Research frontier",
    status: "Open",
    statusClass: "is-research",
    who: "The research that improves the core of intelligence, done in the open.",
    metric: "Open",
    metricLabel: "By default",
    to: "/ilab",
  },
];

const principles = [
  {
    i: "01",
    title: "Evidence over assumption",
    body: "Every signal is deterministic and links back to a primary source. Not summaries. Not inference you cannot check.",
  },
  {
    i: "02",
    title: "Traceable to the source",
    body: "Follow any answer down to the exact filing, record, or document it came from. Verify, do not trust.",
  },
  {
    i: "03",
    title: "Open by default",
    body: "We publish, we cite, and we build in the open, because power you cannot inspect is just a new gatekeeper.",
  },
];

/* Build a realistic cog outline (trapezoidal teeth) centered at (0,0). */
function gearPath(N, Ro, Rd) {
  const w = (2 * Math.PI) / N;
  const P = (r, a) => `${(r * Math.cos(a)).toFixed(2)} ${(r * Math.sin(a)).toFixed(2)}`;
  let d = `M ${P(Rd, 0)}`;
  for (let i = 0; i < N; i++) {
    const a0 = i * w;
    d +=
      ` L ${P(Ro, a0 + w * 0.13)} L ${P(Ro, a0 + w * 0.4)}` +
      ` L ${P(Rd, a0 + w * 0.53)} L ${P(Rd, a0 + w)}`;
  }
  return d + " Z";
}

/* A metallic gunmetal gear: cog body + machined face + hub + bolt holes. */
function Gear({ N, Ro, Rd, hub = 0, bolts = 0, boltR = 0, face = true, grad = "gGun" }) {
  const holes = [];
  for (let i = 0; i < bolts; i++) {
    const a = ((2 * Math.PI) / bolts) * i;
    holes.push(
      <circle
        key={i}
        cx={(boltR * Math.cos(a)).toFixed(2)}
        cy={(boltR * Math.sin(a)).toFixed(2)}
        r={Ro * 0.075}
        fill="#0c0c0f"
        stroke="#3d3d45"
        strokeWidth="0.8"
      />
    );
  }
  return (
    <g filter="url(#gShadow)">
      <path d={gearPath(N, Ro, Rd)} fill={`url(#${grad})`} stroke="#0b0b0d" strokeWidth="1" strokeLinejoin="round" />
      {face && <circle r={Rd * 0.8} fill="url(#gFace)" stroke="#3a3a42" strokeWidth="0.8" />}
      {face && <circle r={Rd * 0.8} fill="none" stroke="#54565f" strokeWidth="0.6" strokeOpacity="0.5" />}
      {holes}
      {hub > 0 && <circle r={hub} fill="#101013" stroke="#44454d" strokeWidth="1" />}
    </g>
  );
}

const CLOCK_PRODUCTS = [
  { label: "StockLens", angle: -90, to: "/projects#stocklens" },
  { label: "ATC", angle: -45, to: "/atc" },
  { label: "MHX", angle: 0, to: "/projects#mhx" },
  { label: "Meg-O", angle: 45, to: "/meg-o" },
  { label: "NetworkX", angle: 90, to: "/projects#networkx" },
  { label: "NoFloat", angle: 135, to: "/nofloat" },
  { label: "Bill-y", angle: 180, to: "/projects#billy" },
  { label: "iLab", angle: 225, to: "/projects#ilab" },
];

// Dim background gears that peek around the engine for mechanical density.
const BG_GEARS = [
  { x: 64, y: 70, N: 13, Ro: 46, Rd: 34, spin: "clk-bg-a" },
  { x: 384, y: 86, N: 11, Ro: 38, Rd: 28, spin: "clk-bg-b" },
  { x: 396, y: 372, N: 14, Ro: 50, Rd: 37, spin: "clk-bg-a" },
  { x: 70, y: 378, N: 10, Ro: 34, Rd: 25, spin: "clk-bg-b" },
];

function ClockMotif() {
  const navigate = useNavigate();
  const C = 220;
  const V = 440;
  const ring = 140;
  const idlerR = 96;
  const rad = (d) => (d * Math.PI) / 180;

  const go = (item) => {
    if (item.href) window.open(item.href, "_blank", "noopener,noreferrer");
    else if (item.to) navigate(item.to);
  };

  // clock tick marks
  const ticks = [];
  for (let i = 0; i < 60; i++) {
    const a = rad(i * 6);
    const outer = 205;
    const inner = i % 5 === 0 ? 190 : 198;
    ticks.push(
      <line
        key={i}
        x1={C + outer * Math.cos(a)}
        y1={C + outer * Math.sin(a)}
        x2={C + inner * Math.cos(a)}
        y2={C + inner * Math.sin(a)}
        stroke={i % 5 === 0 ? "#45464e" : "#2a2a2f"}
        strokeWidth={i % 5 === 0 ? 2.2 : 1}
      />
    );
  }

  return (
    <div className="hero-nodes">
      <svg viewBox={`0 0 ${V} ${V}`} className="hero-nodes-svg" role="group" aria-label="The Intellusion engine with product gears">
        <defs>
          <linearGradient id="gGun" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#50535b" />
            <stop offset="0.45" stopColor="#24262b" />
            <stop offset="1" stopColor="#101013" />
          </linearGradient>
          <linearGradient id="gGunDim" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#30323a" />
            <stop offset="1" stopColor="#0d0d10" />
          </linearGradient>
          <radialGradient id="gFace" cx="0.38" cy="0.34" r="0.78">
            <stop offset="0" stopColor="#34363f" />
            <stop offset="1" stopColor="#111115" />
          </radialGradient>
          <radialGradient id="gRed" cx="0.4" cy="0.34" r="0.85">
            <stop offset="0" stopColor="#ff7a7b" />
            <stop offset="0.5" stopColor="#ff3031" />
            <stop offset="1" stopColor="#b7171d" />
          </radialGradient>
          <linearGradient id="gHand" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#d2d5da" />
            <stop offset="1" stopColor="#64666e" />
          </linearGradient>
          <filter id="gShadow" x="-35%" y="-35%" width="170%" height="170%">
            <feDropShadow dx="0" dy="2.5" stdDeviation="3" floodColor="#000" floodOpacity="0.55" />
          </filter>
          <radialGradient id="gVign" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0.55" stopColor="#ff3031" stopOpacity="0.055" />
            <stop offset="1" stopColor="#ff3031" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* inner glow + clock bezel */}
        <circle cx={C} cy={C} r="200" fill="url(#gVign)" />
        <circle cx={C} cy={C} r="206" fill="none" stroke="#2b2c31" strokeWidth="2" />
        <circle cx={C} cy={C} r="184" fill="none" stroke="#1b1b1f" strokeWidth="1" />
        <g>{ticks}</g>

        {/* background gears — mechanical density behind the engine */}
        {BG_GEARS.map((g, i) => (
          <g key={i} transform={`translate(${g.x} ${g.y})`} opacity="0.5">
            <g className={g.spin}>
              <Gear N={g.N} Ro={g.Ro} Rd={g.Rd} hub={g.Ro * 0.22} bolts={5} boltR={g.Ro * 0.5} grad="gGunDim" />
            </g>
          </g>
        ))}

        {/* central engine — clickable to all products */}
        <g
          className="clk-center"
          role="link"
          tabIndex={0}
          aria-label="See all products"
          onClick={() => navigate("/projects")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/projects");
            }
          }}
          transform={`translate(${C} ${C})`}
        >
          <g className="clk-gear">
            <Gear N={16} Ro={70} Rd={54} hub={0} bolts={8} boltR={40} />
          </g>
          <g className="clk-gear-rev">
            <Gear N={11} Ro={40} Rd={29} hub={0} bolts={5} boltR={20} grad="gGunDim" />
          </g>
        </g>

        {/* clock hands (absolute coords, pivot at centre) */}
        <g className="clk-hand-h">
          <polygon points={`${C},${C - 52} ${C + 4},${C - 2} ${C},${C + 13} ${C - 4},${C - 2}`} fill="url(#gHand)" stroke="#2a2b30" strokeWidth="0.6" />
        </g>
        <g className="clk-hand-m">
          <polygon points={`${C},${C - 92} ${C + 2.8},${C - 2} ${C},${C + 15} ${C - 2.8},${C - 2}`} fill="url(#gHand)" stroke="#2a2b30" strokeWidth="0.6" />
        </g>
        <g className="clk-hand-s">
          <line x1={C} y1={C + 22} x2={C} y2={C - 104} stroke="#ff3031" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx={C} cy={C - 104} r="3" fill="#ff3031" />
        </g>

        {/* red hub cap / centre screw */}
        <circle className="clk-hub-ring" cx={C} cy={C} r="20" fill="none" stroke="#ff3031" strokeOpacity="0.4" strokeWidth="1.5" />
        <circle className="clk-hub" cx={C} cy={C} r="11" fill="url(#gRed)" stroke="#8f1218" strokeWidth="1" />

        {/* orbiting planetary ring: idler gears + product gears + labels */}
        <g className="clk-orbit">
          {CLOCK_PRODUCTS.map((p) => {
            const ix = C + idlerR * Math.cos(rad(p.angle));
            const iy = C + idlerR * Math.sin(rad(p.angle));
            return (
              <g key={`idler-${p.label}`} transform={`translate(${ix} ${iy})`} opacity="0.75">
                <g className="clk-idler">
                  <Gear N={11} Ro={22} Rd={16} hub={5} bolts={0} grad="gGunDim" />
                </g>
              </g>
            );
          })}
          {CLOCK_PRODUCTS.map((p) => {
            const x = C + ring * Math.cos(rad(p.angle));
            const y = C + ring * Math.sin(rad(p.angle));
            const lx = C + (ring + 30) * Math.cos(rad(p.angle));
            const ly = C + (ring + 30) * Math.sin(rad(p.angle));
            return (
              <g
                key={p.label}
                className="clk-prod"
                role="link"
                tabIndex={0}
                aria-label={`Open ${p.label}`}
                onClick={() => go(p)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    go(p);
                  }
                }}
              >
                {/* invisible larger hit area */}
                <circle cx={x} cy={y} r="26" fill="transparent" />
                <g transform={`translate(${x} ${y})`}>
                  <g className="clk-sat">
                    <Gear N={10} Ro={20} Rd={14} hub={0} bolts={0} />
                  </g>
                  <circle className="clk-prod-dot" cx="0" cy="0" r="6" fill="url(#gRed)" stroke="#8f1218" strokeWidth="0.8" />
                </g>
                <text x={lx} y={ly + 4} textAnchor="middle" className="clk-label">
                  {p.label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
      <span className="hero-nodes-cap">The Intellusion engine · click to explore</span>
    </div>
  );
}

function FrontRow({ front }) {
  const content = (
    <>
      <span className="front-idx">{front.index}</span>
      <span className="front-head">
        <span className="front-cat">{front.cat}</span>
        <span className="front-name">
          {front.name}
          <span className={`front-tag ${front.statusClass}`}>{front.status}</span>
        </span>
      </span>
      <span className="front-who">{front.who}</span>
      <span className="front-metric">
        <span className="front-metric-n">{front.metric}</span>
        <span className="front-metric-l">{front.metricLabel}</span>
      </span>
      <span className="front-arrow" aria-hidden="true">
        {front.href || front.to ? "→" : ""}
      </span>
    </>
  );

  if (front.href) {
    return (
      <a className="front-row" href={front.href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }
  if (front.to) {
    return (
      <Link className="front-row" to={front.to}>
        {content}
      </Link>
    );
  }
  return <article className="front-row">{content}</article>;
}

function Home() {
  return (
    <main className="home">
      {/* HERO */}
      <header className="home-hero">
        <div className="home-hero-3d" aria-hidden="true">
          <Suspense fallback={null}>
            <Hero3D />
          </Suspense>
        </div>
        <div className="home-hero-grid" aria-hidden="true" />
        <div className="home-hero-aurora" aria-hidden="true">
          <span className="aur aur-1" />
          <span className="aur aur-2" />
          <span className="aur aur-3" />
        </div>
        <div className="home-hero-particles" aria-hidden="true">
          <span /><span /><span /><span /><span /><span /><span /><span />
        </div>
        <div className="home-hero-glow" aria-hidden="true" />
        <div className="ix-shell home-hero-in">
          <div className="home-hero-copy">
            <span className="ix-eyebrow">Intelligence for everyone</span>
            <h1 className="home-hero-title">
              The advantages the few kept for themselves,{" "}
              <span className="red">now in everyone&rsquo;s hands.</span>
            </h1>
            <p className="home-hero-sub">
              Intellusion builds intelligent systems that put institutional grade
              tools, financial intelligence, research, and trusted networks, in the
              hands of the people they were kept from.{" "}
              <b>Evidence over assumption. Every output traceable to its source.</b>
            </p>
            <div className="home-hero-btns">
              <a href="#products" className="ix-btn ix-btn--primary">
                Explore the products →
              </a>
              <Link to="/about" className="ix-btn ix-btn--ghost">
                Read our mission
              </Link>
            </div>
          </div>
          <ClockMotif />
        </div>
      </header>

      {/* OUTCOMES */}
      <section className="home-outcomes">
        <div className="ix-shell">
          <div className="home-outcomes-grid">
            {outcomes.map((o) => (
              <div className="home-outcome" key={o.label}>
                <div className="home-outcome-n">
                  {o.n}
                  {o.u && <span className="u">{o.u}</span>}
                </div>
                <div className="home-outcome-l">{o.label}</div>
              </div>
            ))}
          </div>
          <p className="home-outcomes-note">
            StockLens figures, measured in production. Every signal links back to a
            primary source.
          </p>
        </div>
      </section>

      {/* THE SHIFT */}
      <section className="home-block home-shift">
        <div className="ix-shell">
          <div className="home-sec-head">
            <span className="ix-eyebrow">The shift</span>
            <h2>
              A story of who gets to <span className="red">know.</span>
            </h2>
          </div>
          <p className="home-shift-quote">
            For a long time, the tools that compound money and influence, the data,
            the research, the networks, lived behind a handful of institutions.{" "}
            <span className="fade">
              Everyone else got the summary, and was asked to trust it.
            </span>{" "}
            We are changing who holds those tools, and proving every answer back to
            the source.
          </p>
        </div>
      </section>

      {/* PRODUCTS AS FRONTS */}
      <section id="products" className="home-block">
        <div className="ix-shell">
          <div className="home-sec-head">
            <span className="ix-eyebrow is-gray">The products</span>
            <h2>Five fronts, one mission.</h2>
            <p>
              Each product hands a capability that used to be gated to the people it
              was gated from.
            </p>
          </div>
          <div className="home-fronts">
            {fronts.map((f) => (
              <FrontRow front={f} key={f.name} />
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="home-block">
        <div className="ix-shell">
          <div className="home-sec-head">
            <span className="ix-eyebrow is-gray">How we build</span>
            <h2>Trust is engineered, not claimed.</h2>
          </div>
          <div className="home-princ">
            {principles.map((p) => (
              <div className="home-pcard" key={p.i}>
                <span className="home-pcard-i">{p.i}</span>
                <h4>{p.title}</h4>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="home-cta">
        <div className="ix-shell">
          <span className="ix-eyebrow">Join the mission</span>
          <h2>Intelligence should not belong to the few.</h2>
          <Link to="/contact" className="ix-btn ix-btn--primary home-cta-btn">
            Get access →
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Home;
